import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import {
  PassportProvider,
  PassportExpander,
  PassportForm
} from '../../passport/Passport';
import { getFormValues } from '../../passport/Passport';
import { Color } from '../../components/ThemeProvider/ThemeStyle';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs';
import SaveButton from './SaveButton';
import {
  serviceProviderModule,
  loadData,
  clearState,
  onCreate,
  onSave
} from './ServiceProviderDucks';
import { Wrapper, ErrorMessage } from './ServiceProviderStyle';
import TextField from '@material-ui/core/TextField';
import Row from '../../passport/Components/Row';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import usePageMode from '../../components/_hooks/usePageMode';
import PageLoading from '../../components/PageLoader/PageLoading';
import { PAGE_MODE, ROLES_ADMIN } from '../../_helpers/Constants';
import useSimpleModal from '../../components/_hooks/useSimpleModal';
import ParentOrganizationModal from './ParentOrganizationModal';
import { IconButton, InputAdornment } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import './ServiceProviderTranslate';
import useHasAccess from '../../components/useHasAccess';
import permissions from '../../_helpers/permissions';
import MyMap from './MyMap/MyMap';
import useUser from '../LoginPage/useUser';

export const validCoordinat = value => {
  if (value === '' || !value) {
    return 'required';
  } else if (value && String(value).length <= 3) {
    return 'noCorrectly';
  }
  return 'correctly';
};

export default function ServiceProviderPage({ match }) {
  const modal = useSimpleModal();
  const { register, handleSubmit } = useForm();
  const pageMode = usePageMode(match);
  const viewMode = !useHasAccess(permissions.serviceProviderPage);
  const { t, i18n } = useTranslation();
  const [parentOrg, setParentOrg] = useState({});
  const user = useUser();
  const [isAdmin, setIsAdmin] = useState(false);

  const dispatch = useDispatch();
  const { loading, info, error, data, metadata } = useSelector(
    state => state[serviceProviderModule]
  );

  const breadCrumbItems = [
    { name: t('breadcrumbs_home'), href: '/' },
    { name: t('breadcrumbs_serviceProviders'), href: '/serviceProviders' },
    { name: info[`${i18n.language}_name`] }
  ];

  const [coordinates, setCoordinates] = useState({
    lat: data.lat,
    lon: data.lon
  });

  const onSubmit = staticValues => {
    let values = { ...staticValues, metadataKey: info.metadataKey };
    if (parentOrg.id) values.parentServiceProviderId = parentOrg.id;

    if (pageMode === PAGE_MODE.EDIT) {
      getFormValues().then(formValues => {
        if (
          formValues &&
          validCoordinat(coordinates.lat) === 'correctly' &&
          'correctly' === validCoordinat(coordinates.lon)
        ) {
          dispatch(onSave({ ...values, id: match.params.id }, formValues, coordinates));
        }
      });
    }

    if (pageMode === PAGE_MODE.CREATE) {
      getFormValues().then(formValues => {
        if (
          formValues &&
          validCoordinat(coordinates.lat) === 'correctly' &&
          'correctly' === validCoordinat(coordinates.lon)
        ) {
          dispatch(onCreate(values, formValues, coordinates));
        }
      });
    }
  };

  useEffect(() => {
    dispatch(loadData(match.params.id));
    user.roles.forEach(item => {
      if (item === ROLES_ADMIN.ES_ADMINS || item === ROLES_ADMIN.ES_GODS) {
        setIsAdmin(true);
      }
    });
    return () => dispatch(clearState());
  }, [dispatch, match.params.id]);

  useEffect(() => {
    setCoordinates({
      lat: data.lat,
      lon: data.lon
    });
    setParentOrg(info.parentOrg);
  }, [info.parentOrg]);

  if (error) {
    return (
      <>
        <BreadCrumbs items={breadCrumbItems} />
        <Paper className="p3">
          <ErrorMessage>
            <code>{error}</code>
          </ErrorMessage>
        </Paper>
      </>
    );
  }

  return (
    <PageLoading loading={loading}>
      <BreadCrumbs items={breadCrumbItems} />
      <Wrapper className="p3">
        <ErrorBoundary>
          <div className="title">{info[`${i18n.language}_name`]}</div>
          <Divider />
          <div style={{ transform: 'translateY(30px)' }}>
            <span className="bold">{t('serviceProvider_id')}</span>: {info.id}
          </div>
          <PassportProvider
            info={info}
            withoutScroll={false}
            stickyTop={Color.headerHeight}
            meta={metadata}
            data={{ metadataKey: metadata.id, ...data }}
            config={{
              documentsProps: {
                mimeType: 'image/jpeg,image/png',
                maxSize: 10000000
              }
            }}
          >
            <PassportExpander />
            <PassportForm
              disabled={viewMode}
              info={info}
              betweenRender={(_, nextQuestionCode) => {
                if (nextQuestionCode === 'service_provider.ru_name') {
                  return (
                    <>
                      <Row label={t('serviceProvider_bin')}>
                        <TextField
                          disabled={viewMode}
                          placeholder={viewMode ? '' : t('serviceProvider_bin')}
                          defaultValue={info.bin}
                          {...register('bin')}
                        />
                      </Row>
                      <Row label={t('serviceProvider_iin')}>
                        <TextField
                          disabled={viewMode}
                          placeholder={viewMode ? '' : t('serviceProvider_iin')}
                          defaultValue={info.iin}
                          {...register('iin')}
                        />
                      </Row>
                      <Row label={t('serviceProvider_nedbId')}>
                        <TextField
                          disabled={viewMode}
                          placeholder={viewMode ? '' : t('serviceProvider_nedbId')}
                          defaultValue={info.nedbId}
                          {...register('nedbId')}
                        />
                      </Row>
                      <Row label={t('serviceProvider_parent')}>
                        <TextField
                          value={parentOrg[`${i18n.language}_name`] || ''}
                          onClick={() => modal.open(true)}
                          disabled={viewMode}
                          placeholder={viewMode ? '' : t('serviceProvider_parent')}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  disabled={viewMode}
                                  className="show-on-hover"
                                  onClick={e => {
                                    e.stopPropagation();
                                    setParentOrg({});
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Row>
                    </>
                  );
                }
                if (nextQuestionCode === 'service_provider.lang_education'  && isAdmin) {
                  const showWarning =
                    info.futureMonthParentAttendanceConfirmation?.isEnable === false &&
                    info.enableParentAttendance=== true;
                  const defaultChecked =
                    info.futureMonthParentAttendanceConfirmation?.isEnable === false
                      ? false
                      : !!info.enableParentAttendance;

                  return (
                    <Row label={t('serviceProvider_enableParentAttendance')}>
                      <div
                        style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                      >
                        <label
                          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                          <input
                            type="checkbox"
                            disabled={viewMode}
                            defaultChecked={defaultChecked}
                            {...register('enableParentAttendance')}
                          />
                          {t('serviceProvider_confirmParentAttendance')}
                        </label>
                        {showWarning && (
                          <span style={{ color: 'red', fontSize: '0.8rem' }}>
                            {t('serviceProvider_unableParentAttendance')}
                          </span>
                        )}
                      </div>
                    </Row>
                  );
                }
                return null;
              }}
            />
            {isAdmin && (
              <MyMap setCoordinates={setCoordinates} coordinates={coordinates} />
            )}
            {modal.isOpen && (
              <ParentOrganizationModal
                parentOrg={parentOrg}
                modal={modal}
                onSelect={row => {
                  setParentOrg(row);
                  modal.close();
                }}
              />
            )}
            {[PAGE_MODE.EDIT, PAGE_MODE.CREATE].includes(pageMode) && (
              <SaveButton onClick={handleSubmit(onSubmit)} pageMode={pageMode} />
            )}
          </PassportProvider>
        </ErrorBoundary>
      </Wrapper>
    </PageLoading>
  );
}
